import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import PermissionItemView from 'components/permission-item-view';
import st from 'core/shared-translations';
import classes from './clientedit.scss';
import commonClass from "styles/_base.scss";
import ClientSecretCreateContainer from "../containers/client-secret-create-container";
import { Box } from '@mui/material';
import { AlertV2, Switch, Breadcrumb, InputField, BreadcrumbItem, Chip, Typography, TextBadge, Button, Checkbox, IconButton, Dialog, TooltipV3, MenuSeparator, Menu, MenuItem } from '@getgo/chameleon-web-react-wrapper';
import { EditOutlinedIcon, PersonOutlinedIcon, TrashActiveIcon, ChevronLeftFilledIcon, CopyOutlinedIcon, CloseOutlinedIcon } from "@getgo/chameleon-icons/react";
import environment from "lib/environment";
import BoxedContainer from 'components/boxed-container-view';
import ResponsiveDialog from 'components/dialog-view.js';
import ClientDetailsTab from './client-details-tab';
import KeyValView from 'components/key-val-view';
import ClientOwnersTab from './client-owners-tab';
import ClientRolesTab from './client-roles-tab';
import ClientImplicitScopesTab from './client-implicit-scopes-tab';
import ClientRequiredScopesTab from './client-required-scopes-tab';
import ClientScopesAdminTab from './client-scopes-admin-tab';
const oauthClients = environment.get().oauth.redirect_url;

const t = defineMessages({
  'client-info-view.no.description': {
    id: 'client-info-view.no.description',
    defaultMessage: 'No description',
    description: 'Text to display when no description is set for a client',
  },
  'client-edit.field.description.title': {
    id: 'client-edit.field.description.title',
    defaultMessage: 'Description',
    description: 'Label for the optional description field',
  },
  'client-edit.add.title': {
    id: 'client-edit.add.title',
    defaultMessage: 'Create client',
    description: 'Title for adding a client in client details',
  },
  'client-edit.edit.title': {
    id: 'client-edit.edit.title',
    defaultMessage: 'Edit client details',
    description: 'Title for editing a client in client details',
  },
  'client-edit.tab.details': {
    id: 'client-edit.tab.details',
    defaultMessage: 'Details',
    description: 'Details tab text',
  },
  'client-edit.tab.scopes': {
    id: 'client-edit.tab.scopes',
    defaultMessage: 'Scopes',
    description: 'Scopes tab text',
  },
  'client-edit.tab.scopes.subheading': {
    id: 'client-edit.tab.scopes.subheading',
    defaultMessage: 'Select the permissions or access level of an application or user for this client.',
    description: 'Scopes tab subheading text',
  },
  'client-edit.tab.required.scopes': {
    id: 'client-edit.tab.required.scopes',
    defaultMessage: 'Required scopes',
    description: 'Required scopes tab text',
  },
  'client-edit.tab.required.scopes.subheading1': {
    id: 'client-edit.tab.required.scopes.subheading1',
    defaultMessage: 'Select which scopes are required for a token to be considered valid for this client.',
    description: 'Required scopes tab subheading text',
  },
  'client-edit.tab.required.scopes.subheading2': {
    id: 'client-edit.tab.required.scopes.subheading2',
    defaultMessage: '(When scope validation is enabled, all required scopes must be present.)',
    description: 'Required scopes tab second subheading text',
  },
  'client-edit.tab.implicit.scopes': {
    id: 'client-edit.tab.implicit.scopes',
    defaultMessage: 'Implicit scopes',
    description: 'Implicit scopes tab text',
  },
  'client-edit.tab.implicit.scopes.subheading': {
    id: 'client-edit.tab.implicit.scopes.subheading',
    defaultMessage: 'Select which scopes are always added to the authorization request',
    description: 'Implicit scopes tab subheading text',
  },
  'client-edit.tab.restricted.scopes': {
    id: 'client-edit.tab.restricted.scopes',
    defaultMessage: 'Restricted scopes',
    description: 'Restricted scopes tab text',
  },
  'client-edit.tab.restricted.scopes.subheading': {
    id: 'client-edit.tab.restricted.scopes.subheading',
    defaultMessage: 'Scopes that require review and authorization from GoTo.',
    description: 'Restricted scopes tab subheading text',
  },
  'client-edit.tab.edit-scopes': {
    id: 'client-edit.tab.edit-scopes',
    defaultMessage: 'Edit client scopes',
    description: 'Scopes tab text',
  },
  'client-edit.field.appName': {
    id: 'client-edit.field.appName',
    defaultMessage: 'Client name',
    description: 'Label for the Name field',
  },
  'client-edit.permissions.instructions': {
    id: 'client-edit.permissions.instructions',
    defaultMessage: 'Select the scopes this client will need to request. The user getting access will need product or admin privileges.',
    description: 'Instruction text for the top of the Permissions tab',
  },
  'client-edit.credentials.client.delete.title': {
    id: 'client-edit.credentials.client.delete.title',
    defaultMessage: 'Delete client',
    description: 'Title text for Credentials - Delete client',
  },
  'client-edit.credentials.client.disable.title': {
    id: 'client-edit.credentials.client.disable.title',
    defaultMessage: 'Disable client',
    description: 'Title text for Credentials - Disable client',
  },
  'client-edit.credentials.client.enable.title': {
    id: 'client-edit.credentials.client.enable.title',
    defaultMessage: 'Enable client',
    description: 'Title text for Credentials - Enable client',
  },
  'client-edit.credentials.client.delete.text': {
    id: 'client-edit.credentials.client.delete.text',
    defaultMessage: 'Deleted clients cannot be recovered.',
    description: 'Body text Credentials - Delete client',
  },
  'client-edit.copy.client.secret': {
    id: 'client-edit.copy.client.secret',
    defaultMessage: 'Copy client secret.',
    description: 'Title for copy client secret button',
  },
  'client-edit.profile.message': {
    id: 'client-edit.profile.message',
    defaultMessage: 'Get user information, modify user details',
    description: 'Title for profile chip',
  },
  'client-edit.jive.message': {
    id: 'client-edit.jive.message',
    defaultMessage: 'All GoTo Connect advanced permissions',
    description: 'Title for goto connect chip',
  },
  'client-create.add.title': {
    id: 'client-create.add.title',
    defaultMessage: 'Create client',
    description: 'Title for adding a client in client details',
  },
  'client-create.tab.details': {
    id: 'client-create.tab.details',
    defaultMessage: 'Client details',
    description: 'Details tab text',
  },
  "create-token.tab.copy-and-store": {
    id: "create-token.tab.copy-and-store",
    defaultMessage: "Copy and store your credentials",
    description:
      "Title for personal access token tab copy and store your token",
  },
  "create-token.tab.cancel": {
    id: "create-token.tab.cancel",
    defaultMessage: "Cancel",
    description: "Title for personal access token cancel button",
  },
  "create-token.tab.next": {
    id: "create-token.tab.next",
    defaultMessage: "Next",
    description: "Title for personal access token cancel next",
  },
  "create-token.tab.save": {
    id: "create-token.tab.save",
    defaultMessage: "Save",
    description: "Title for personal access token save button",
  },
  "create-token.credentials.copy-display-text": {
    id: "create-token.credentials.copy-display-text",
    defaultMessage:
      "The secret will only be shown once.{lineBreak}Make sure to copy it and store it in a secure place.",
    description: "Label for token value copy",
  },
  "create-token.token.copy.confirm": {
    id: "create-token.token.copy.confirm",
    defaultMessage: "I've copied and stored this token",
    description:
      "Body text Credentials - Text for the confirm secret copy check box label.",
  },
  "create-token.validation.name-required": {
    id: "create-token.validation.name-required",
    defaultMessage: "Enter a token name.",
    description: "Content of the error message when Name field is empty",
  },
  "create-token.validation.name-exceeds-max-length": {
    id: "create-token.validation.name-exceeds-max-length",
    defaultMessage: "Name can't be more than 30 characters.",
    description:
      "Content of the error message when the entry in the Name field exceeds more than 30 characters",
  },
  'client-edit.tab.ownershp': {
    id: 'client-edit.tab.ownershp',
    defaultMessage: 'Ownership',
    description: 'Ownership tab text',
  },
  'client-edit.tab.token.validity': {
    id: 'client-edit.tab.token.validity',
    defaultMessage: 'Token validity',
    description: 'Token validity tab text',
  },
  'client-edit.tab.cas.theme': {
    id: 'client-edit.tab.cas.theme',
    defaultMessage: 'CAS theme',
    description: 'CAS theme tab text',
  },
  'client-edit.tab.theme.title': {
    id: 'client-edit.tab.theme.title',
    defaultMessage: 'Theme',
    description: 'Theme tab text',
  },
  'client-edit.tab.login.theme': {
    id: 'client-edit.tab.login.theme',
    defaultMessage: 'Login theme',
    description: 'Login theme tab text',
  },
  'client-edit.tab.login.theme.subheading': {
    id: 'client-edit.tab.login.theme.subheading',
    defaultMessage: 'Enter the name that will appear on the login page theme.',
    description: 'Login theme subheading text',
  },
  'client-edit.tab.tenant': {
    id: 'client-edit.tab.tenant',
    defaultMessage: 'Tenant',
    description: 'Tenant tab text',
  },
  'client-edit.tab.tenant.subheading': {
    id: 'client-edit.tab.tenant.subheading',
    defaultMessage: 'Restrict this client’s access token to a specific tenant if multiple tenants have a conflict.',
    description: 'Tenant tab subheading text',
  },
  'client-edit.tab.grant.types': {
    id: 'client-edit.tab.grant.types',
    defaultMessage: 'Grant types',
    description: 'Grant types tab text',
  },

  'client-edit.ownership.title': {
    id: 'client-edit.ownership.title',
    defaultMessage: 'Ownership',
    description: 'Title for the Ownership section',
  },
  'client-edit.owners.modal.title': {
    id: 'client-edit.owners.modal.title',
    defaultMessage: 'Edit owners',
    description: 'title for the Edit owners modal dialog',
  },

  'client-edit.roles.modal.title': {
    id: 'client-edit.roles.modal.title',
    defaultMessage: 'Edit roles',
    description: 'title for the Edit roles modal dialog',
  },

  'client-edit.implicit-scopes.modal.title': {
    id: 'client-edit.implicit-scopes.modal.title',
    defaultMessage: 'Edit implicit scopes',
    description: 'title for the Edit implicit scopes modal dialog',
  },

  'client-edit.required-scopes.modal.title': {
    id: 'client-edit.required-scopes.modal.title',
    defaultMessage: 'Edit required scopes',
    description: 'title for the Edit required scopes modal dialog',
  },

  'client-edit.scopes.modal.title': {
    id: 'client-edit.scopes.modal.title',
    defaultMessage: 'Edit scopes',
    description: 'title for the Edit scopes modal dialog',
  },

  'client-edit.ownership.owner.description': {
    id: 'client-edit.ownership.owner.description',
    defaultMessage: 'Owner of this OAuth client',
    description: 'Ownership main description',
  },
  'client-edit.tokens.title': {
    id: 'client-edit.tokens.title',
    defaultMessage: 'Tokens',
    description: 'Title for the Tokens section',
  },
  'client-edit.tokens.access.validity.label': {
    id: 'client-edit.tokens.access.validity.label',
    defaultMessage: 'Access token validity',
    description: 'Tokens section: heading for access token validity',
  },
  'client-edit.tokens.access.validity.help': {
    id: 'client-edit.tokens.access.validity.help',
    defaultMessage: 'Enter how long an access token for this client will be valid. (Max 24 hours)',
    description: 'Help text for access token validity',
  },
  'client-edit.tokens.refresh.validity.label': {
    id: 'client-edit.tokens.refresh.validity.label',
    defaultMessage: 'Refresh token validity',
    description: 'Tokens section: heading for refresh token validity',
  },
  'client-edit.tokens.refresh.validity.help': {
    id: 'client-edit.tokens.refresh.validity.help',
    defaultMessage: 'Enter how long a refresh token for this client will be valid. (Max 30 days for non-admins)',
    description: 'Help text for refresh token validity',
  },
  'client-edit.authorization.title': {
    id: 'client-edit.authorization.title',
    defaultMessage: 'Authorization',
    description: 'Title for Authorization section',
  },
  'client-edit.authorization.auto-approval.label': {
    id: 'client-edit.authorization.auto-approval.label',
    defaultMessage: 'Auto approval',
    description: 'Label for Auto approval switch',
  },
  'client-edit.authorization.auto-approval.help': {
    id: 'client-edit.authorization.auto-approval.help',
    defaultMessage: 'Bypass the consent page when authorizing code or implicit scopes.',
    description: 'Help text for Auto approval',
  },
  'client-edit.authorization.confidential-credentials.label': {
    id: 'client-edit.authorization.confidential-credentials.label',
    defaultMessage: 'Confidential credentials',
    description: 'Label for confidential credentials switch',
  },
  'client-edit.authorization.confidential-credentials.help': {
    id: 'client-edit.authorization.confidential-credentials.help',
    defaultMessage: 'Select if your app can keep its client secret private.',
    description: 'Help text for confidential credentials',
  },
  'client-edit.authorization.scope-validation.label': {
    id: 'client-edit.authorization.scope-validation.label',
    defaultMessage: 'Scope validation',
    description: 'Label for scope validation switch',
  },
  'client-edit.authorization.scope-validation.help': {
    id: 'client-edit.authorization.scope-validation.help',
    defaultMessage: 'Require that scopes be present for a token to be considered valid for this client.',
    description: 'Help text for scope validation',
  },
  'client-edit.authorization.access-token-restriction.label': {
    id: 'client-edit.authorization.access-token-restriction.label',
    defaultMessage: 'Access token restriction',
    description: 'Label for access token restriction switch',
  },
  'client-edit.authorization.access-token-restriction.help': {
    id: 'client-edit.authorization.access-token-restriction.help',
    defaultMessage: 'Do not allow this client to generate access tokens.',
    description: 'Help text for access token restriction',
  },
  'client-edit.grant-types.title': {
    id: 'client-edit.grant-types.title',
    defaultMessage: 'Grant types',
    description: 'Title for Grant types section',
  },
  'client-edit.grant-types.implicit.label': {
    id: 'client-edit.grant-types.implicit.label',
    defaultMessage: 'Implicit',
    description: 'Label for Implicit grant type',
  },
  'client-edit.grant-types.implicit.help': {
    id: 'client-edit.grant-types.implicit.help',
    defaultMessage: 'Allow single page applications (SPAs) or mobile apps to obtain access tokens directly (discouraged).',
    description: 'Help text for Implicit grant type',
  },
  'client-edit.grant-types.authorization-code.label': {
    id: 'client-edit.grant-types.authorization-code.label',
    defaultMessage: 'Authorization code',
    description: 'Label for Authorization code grant type',
  },
  'client-edit.grant-types.authorization-code.help': {
    id: 'client-edit.grant-types.authorization-code.help',
    defaultMessage: 'Allow web or mobile apps to exchange an authorization code for an access token securely.',
    description: 'Help text for Authorization code grant type',
  },
  'client-edit.grant-types.refresh-token.label': {
    id: 'client-edit.grant-types.refresh-token.label',
    defaultMessage: 'Refresh token',
    description: 'Label for Refresh token grant type',
  },
  'client-edit.grant-types.refresh-token.help': {
    id: 'client-edit.grant-types.refresh-token.help',
    defaultMessage: 'Allow clients to obtain a new access token without re-authenticating the user.',
    description: 'Help text for Refresh token grant type',
  },
  'client-edit.grant-types.password.label': {
    id: 'client-edit.grant-types.password.label',
    defaultMessage: 'Password',
    description: 'Label for Password grant type',
  },
  'client-edit.grant-types.password.help': {
    id: 'client-edit.grant-types.password.help',
    defaultMessage: 'Allow the client to exchange a user’s username and password for an access token (discouraged).',
    description: 'Help text for Password grant type',
  },
  'client-edit.grant-types.client-credentials.label': {
    id: 'client-edit.grant-types.client-credentials.label',
    defaultMessage: 'Client credentials',
    description: 'Label for Client credentials grant type',
  },
  'client-edit.grant-types.client-credentials.help': {
    id: 'client-edit.grant-types.client-credentials.help',
    defaultMessage: 'Grant credentials used for machine-to-machine communication.',
    description: 'Help text for Client credentials grant type',
  },
  'client-edit.grant-types.pat.label': {
    id: 'client-edit.grant-types.pat.label',
    defaultMessage: 'Personal access token',
    description: 'Label for Personal access token grant type',
  },
  'client-edit.grant-types.pat.help': {
    id: 'client-edit.grant-types.pat.help',
    defaultMessage: 'Allow users to manually generate a token to access APIs without going through the full OAuth flow.',
    description: 'Help text for personal access token grant type',
  },
  'client-edit.tokens.input.suffix.seconds': {
    id: 'client-edit.tokens.input.suffix.seconds',
    defaultMessage: 'Seconds',
    description: 'Suffix for token validity input fields',
  },
  'client-edit.tab.authorities': {
    id: 'client-edit.tab.authorities',
    defaultMessage: 'Authorities',
    description: 'Authorities tab text',
  },
  'client-edit.authorities.roles.title': {
    id: 'client-edit.authorities.roles.title',
    defaultMessage: 'Roles',
    description: 'Title for Roles section',
  },
  'client-edit.authorities.roles.subheading': {
    id: 'client-edit.authorities.roles.subheading',
    defaultMessage: 'Select which roles are associated with this client.',
    description: 'Subheading for Roles section',
  }

});

const ClientEdit = ({
  isEditMode,
  client,
  errors,
  tabClick,
  onCancel,
  onChange,
  onRedirectUriChange,
  onBlur,
  onSave,
  onDisable,
  onDelete,
  onDoneClick,
  isDone,
  onConfirmSecretCopy,
  enableCTAs,
  canCancel,
  detailsContentsClass,
  permissionsContentsClass,
  credentialsContentsClass,
  detailsButtonClass,
  permissionsButtonClass,
  credentialsButtonClass,
  permissionsList,
  togglePermissionsDetail,
  addRedirectURI,
  isRedirectUriValid,
  onAddUriClick,
  onDeleteUriClick,
  permissionCheckboxClick,
  permissionGroupCheckboxClick,
  onCopy,
  newClientCredentials,
  isLoading,
  navToClientList,
  onDetailsEditClick,
  openDetailsEditModel,
  onPermissionsEditClick,
  openPermissionsEditModel,
  closeEditModel,
  clientData,
  canWrite,
  disableSubmit,
  meLocale,
  onOwnershipEditClick,
  openOwnershipEditModel,
  onRolesEditClick,
  openRolesEditModel,
  owners,
  sideNavTop,
  sideNavRef,
  sectionRefs,
  onMenuClick,
  activeSection,
  onGrantTypeChange,
  onAuthorizationChange,
  onAddRole,
  onDeleteRole,
  onImplicitScopesEditClick,
  openImplicitScopesEditModel,
  onAddImplicitScope,
  onDeleteImplicitScope,
  onRequiredScopesEditClick,
  openRequiredScopesEditModel,
  onAddRequiredScope,
  onDeleteRequiredScope,
  onScopesEditClick,
  openScopesEditModel,
  onAddScope,
  onDeleteScope,
  rawScopeList,
  scopeValidationErrors,
  clearScopeError,
  addOwnerEmail,
  onOwnerEmailChange,
  onOwnerEmailBlur,
  onAddOwner,
  isOwnerEmailValid,
  onDeleteOwner,
  userType,
  restrictedScopes
}) => {
  const intl = useIntl();
  const menuCollection = [
    {
      key: "Details",
      content: <FormattedMessage {...t['client-edit.tab.details']} />,
      hideMenuItem: false,
    },
    {
      key: "Ownership",
      content: <FormattedMessage {...t['client-edit.tab.ownershp']} />,
      hideMenuItem: false,
    },
    {
      key: "Tokens",
      content: <FormattedMessage {...t['client-edit.tokens.title']} />,
      hideMenuItem: !userType.isAdmin,
    },
    {
      key: "Theme",
      content: <FormattedMessage {...t['client-edit.tab.theme.title']} />,
      hideMenuItem: !userType.isAdmin,
    },
    {
      key: "Authorization",
      content: <FormattedMessage {...t['client-edit.authorization.title']} />,
      hideMenuItem: !userType.isAdmin,
    },
    {
      key: "Scopes",
      content: <FormattedMessage {...t['client-edit.tab.scopes']} />,
      hideMenuItem: false,
    },
    {
      key: "Authorities",
      content: <FormattedMessage {...t['client-edit.tab.authorities']} />,
      hideMenuItem: !userType.isAdmin,
    },
    {
      key: "Grant_types",
      content: <FormattedMessage {...t['client-edit.tab.grant.types']} />,
      hideMenuItem: false,
    },
    {
      key: "Secrets",
      content: intl.formatMessage(st["shared.client-edit.credentials.client.secrets.title"]),
      hideMenuItem: !userType.isOwnedAccount,
    }
  ];

  return (
    <React.Fragment>
      {isEditMode ?
        <Box id="client-edit-container" className={commonClass["clientEditView"]}>
          <Box>
            <Breadcrumb>
              <BreadcrumbItem href={oauthClients}>
                <FormattedMessage {...st['shared.oauthclients']} />
              </BreadcrumbItem>
              <BreadcrumbItem href="#">{client.name}</BreadcrumbItem>
            </Breadcrumb>
            <Box className={classes['clientEditTitle']}>
              <Box style={{ paddingTop: "15px", display: "flex", alignItems: "center" }}>
                <Typography tag="h1" variant="heading-large">
                  {client.name}
                </Typography>
                <TextBadge>{client.disabled ?
                  <FormattedMessage {...st['shared.disabled']} /> :
                  <FormattedMessage {...st['shared.enabled']} />}</TextBadge>
              </Box>
              <Box className={classes['clientEditCtas']}>
                {userType.isAdmin && <Button
                  data-testid="disableClientBtn"
                  variant="tertiary"
                  className={classes["createTokenCta"]}
                  onClick={onDisable}
                >
                  <Typography variant="button-medium">
                    <FormattedMessage {...t[client.disabled ? "client-edit.credentials.client.enable.title" : "client-edit.credentials.client.disable.title"]} />
                  </Typography>
                </Button>}
                <Box className={classes['deleteClientBtn']}>
                  <Button
                    data-testid="deleteClientBtn"
                    variant="danger"
                    className={classes["createTokenCta"]}
                    leadingIcon={<TrashActiveIcon />}
                    onClick={onDelete}
                  >
                    <Typography color="type-color-danger" variant="button-medium">
                      <FormattedMessage {...t["client-edit.credentials.client.delete.title"]} />
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          <MenuSeparator className={commonClass['seperator']} />
          <Box className={commonClass['mainContainer']}>
            <Box ref={sideNavRef} className={commonClass['menuColumn']} style={{ top: `${sideNavTop}px` }}>
              <Menu label="Navigation menu">
                {menuCollection.map(({ content, key, hideMenuItem }) =>
                  !hideMenuItem && (
                    <MenuItem
                      key={key}
                      onClick={() => onMenuClick(key)}
                      className={`${classes['menuItemCustom']} ${activeSection === key ? classes['selected'] : ""}`}
                    >
                      {content}
                    </MenuItem>
                  ))}
              </Menu>
            </Box>
            <Box className={commonClass['clientEditContent']} flex={1}>
              {/* details sections */}
              <BoxedContainer
                title={<FormattedMessage {...t['client-edit.tab.details']} />}
                buttonIcon={<EditOutlinedIcon data-testid="details-edit" />}
                buttonText={intl.formatMessage(st["shared.edit"])}
                onClick={onDetailsEditClick}
                boxIcon={<PersonOutlinedIcon color="#000000" />}
                ref={sectionRefs['Details']}
              >
                <Box className={classes['keyValContainer']}>
                  <Box style={{ paddingBottom: "15px" }}>
                    <Typography variant="caption-medium" color="type-color-secondary">
                      <FormattedMessage {...st['shared.client-id']} />
                    </Typography>
                    <Typography variant="body-medium" color="type-color-primary">
                      {client.clientId}
                    </Typography>
                  </Box>
                  <Box style={{ paddingBottom: "15px" }}>
                    <Typography variant="caption-medium" color="type-color-secondary">
                      <FormattedMessage {...t['client-edit.field.appName']} />
                    </Typography>
                    <Typography variant="body-medium" color="type-color-primary">
                      {client.name}
                    </Typography>
                  </Box>
                  {!client.description &&
                    <Box style={{ paddingBottom: "15px" }}>
                      <Typography variant="caption-medium" color="type-color-secondary">
                        <FormattedMessage {...t['client-info-view.no.description']} />
                      </Typography>
                    </Box>}
                  {client.description ? <Box style={{ paddingBottom: "15px" }}>
                    <Typography variant="caption-medium" color="type-color-secondary">
                      <FormattedMessage {...t['client-edit.field.description.title']} />
                    </Typography>
                    <Typography variant="body-medium" color="type-color-primary">
                      {client.description}
                    </Typography>
                  </Box> : ""}
                  <Box>
                    <Typography variant="caption-medium" color="type-color-secondary">
                      <FormattedMessage {...st['shared.redirectUri.title']} />
                    </Typography>
                    {!!client && !!client.redirectUris && !!client.redirectUris.length &&
                      client.redirectUris.map((redirectUri) => (
                        <Typography key={redirectUri} variant="body-medium" color="type-color-primary">
                          {redirectUri}
                        </Typography>
                      ))}
                  </Box>
                </Box>
              </BoxedContainer>
              <MenuSeparator className={commonClass['seperator']} />
              {/* ownership section */}
              <BoxedContainer
                title={<FormattedMessage {...t['client-edit.ownership.title']} />}
                buttonIcon={<EditOutlinedIcon data-testid="ownership-edit" />}
                buttonText={intl.formatMessage(st["shared.edit"])}
                onClick={onOwnershipEditClick}
                ref={sectionRefs['Ownership']}
              >
                {((client?.owners && client.owners.length > 0) || (owners && owners.length > 0)) &&
                  <Box>
                    <Typography variant="caption-medium" color="type-color-secondary">
                      <FormattedMessage {...t['client-edit.ownership.owner.description']} />
                    </Typography>
                    {(client?.owners?.length ? client.owners : owners).map((owner, index) =>
                      <Typography key={index} variant="body-small">
                        {owner.email}
                      </Typography>
                    )}
                  </Box>}
              </BoxedContainer>
              <MenuSeparator className={commonClass['seperator']} />
              {/*Tokens*/}
              {userType.isAdmin && <React.Fragment>
                <BoxedContainer
                  ref={sectionRefs['Tokens']}
                  title={<FormattedMessage {...t['client-edit.tokens.title']} />}
                  showCTAButton={false}
                >
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.tokens.access.validity.label']} />}
                      subheading={<FormattedMessage {...t['client-edit.tokens.access.validity.help']} />}
                    />
                    <InputField type="number" value={client?.accessTokenValidity} onChange={onChange} name="accessTokenValidity" id="accessTokenValidity">
                      <Typography variant="caption-medium-strong" color="type-color-primary">
                        <FormattedMessage {...t['client-edit.tokens.input.suffix.seconds']} />
                      </Typography>
                    </InputField>
                  </Box>
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.tokens.refresh.validity.label']} />}
                      subheading={<FormattedMessage {...t['client-edit.tokens.refresh.validity.help']} />}
                    />
                    <InputField type="number" value={client?.refreshTokenValidity} onChange={onChange} name="refreshTokenValidity" id="refreshTokenValidity">
                      <Typography variant="caption-medium-strong" color="type-color-primary">
                        <FormattedMessage {...t['client-edit.tokens.input.suffix.seconds']} />
                      </Typography>
                    </InputField>
                  </Box>
                </BoxedContainer>
                <MenuSeparator className={commonClass['seperator']} />
              </React.Fragment>}
              {/*Theme section*/}
              {userType.isAdmin && <React.Fragment>
                <BoxedContainer
                  title={<FormattedMessage {...t['client-edit.tab.theme.title']} />}
                  showCTAButton={false}
                  ref={sectionRefs['Theme']}
                >
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.tab.login.theme']} />}
                      subheading={<FormattedMessage {...t['client-edit.tab.login.theme.subheading']} />}
                    />
                    <InputField value={client?.theme} onChange={onChange} name="theme" id="theme" />
                  </Box>
                </BoxedContainer>
                <MenuSeparator className={commonClass['seperator']} />
              </React.Fragment>}
              {/*Authorization*/}
              {userType.isAdmin && <React.Fragment>
                <BoxedContainer
                  title={<FormattedMessage {...t['client-edit.authorization.title']} />}
                  showCTAButton={false}
                  ref={sectionRefs['Authorization']}
                >
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.tab.tenant']} />}
                      subheading={<FormattedMessage {...t['client-edit.tab.tenant.subheading']} />}
                    />
                    <InputField value={client?.tenant} onChange={onChange} name="tenant" id="tenant" />
                  </Box>
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.authorization.auto-approval.label']} />}
                      subheading={<FormattedMessage {...t['client-edit.authorization.auto-approval.help']} />}
                    />
                    <Switch disabled={false} checked={client?.autoApproved || false} onChange={onAuthorizationChange("autoApproved")} />
                  </Box>
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.authorization.confidential-credentials.label']} />}
                      subheading={<FormattedMessage {...t['client-edit.authorization.confidential-credentials.help']} />}
                    />
                    <Switch disabled={false} checked={client?.confidential || false} onChange={onAuthorizationChange("confidential")} />
                  </Box>
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.authorization.scope-validation.label']} />}
                      subheading={<FormattedMessage {...t['client-edit.authorization.scope-validation.help']} />}
                    />
                    <Switch disabled={false} checked={client?.scopeValidation || false} onChange={onAuthorizationChange("scopeValidation")} />
                  </Box>
                </BoxedContainer>
                <MenuSeparator className={commonClass['seperator']} />
              </React.Fragment>}
              {/* scopes section */}
              <BoxedContainer
                title={<FormattedMessage {...t['client-edit.tab.scopes']} />}
                buttonIcon={<EditOutlinedIcon data-testid="permissions-edit" />}
                buttonText={intl.formatMessage(st["shared.edit"])}
                onClick={userType.isAdmin ? onScopesEditClick : onPermissionsEditClick}
                ref={sectionRefs['Scopes']}
                boxIcon={<PersonOutlinedIcon color="#000000" />}>
                <Box>
                  <KeyValView
                    topBottomMargin
                    heading={<FormattedMessage {...t['client-edit.tab.scopes']} />}
                    subheading={<FormattedMessage {...t['client-edit.tab.scopes.subheading']} />}
                  />
                  {clientData.permissionsList && !!Object.keys(clientData.permissionsList).length &&
                    Object.keys(clientData.permissionsList).map((keyName, index) => (
                      <React.Fragment key={index}>
                        {clientData.permissionsList[keyName].allScopesSelected &&
                          <Chip id={keyName} className={classes["chip"]} size="small">
                            <FormattedMessage {...st[clientData.permissionsList[keyName].parentScopeTranslationId]} />
                          </Chip>}
                        {!clientData.permissionsList[keyName].allScopesSelected &&
                          <Chip size="small" id={keyName} className={classes["chip"]}>
                            <FormattedMessage
                              {...st['shared.client.info.selection.count']}
                              values={{
                                product: intl.formatMessage(st[clientData.permissionsList[keyName].parentScopeTranslationId]),
                                selected: clientData.permissionsList[keyName].numberOfScopesSelected,
                                total: clientData.permissionsList[keyName].numberOfScopesInProduct
                              }}
                            />
                          </Chip>}
                        {(keyName === "jive" || keyName === "profile") && <TooltipV3 triggerId={keyName}>
                          <FormattedMessage {...t[`client-edit.${keyName}.message`]} />
                        </TooltipV3>}
                      </React.Fragment>))}
                </Box>
                {/* required scopes */}
                {userType.isAdmin && <Box>
                  <Box className={classes['alignTwoItemsSideWays']}>
                    <KeyValView
                      topBottomMargin
                      heading={<FormattedMessage {...t['client-edit.tab.required.scopes']} />}
                      subheading={
                        <Box>
                          <FormattedMessage {...t['client-edit.tab.required.scopes.subheading1']} /> <br />
                          <FormattedMessage {...t['client-edit.tab.required.scopes.subheading2']} />
                        </Box>
                      }
                    />
                    <Button
                      data-testid="required-scope-button"
                      role="button"
                      onClick={onRequiredScopesEditClick}
                      leadingIcon={<EditOutlinedIcon data-testid="required-permissions-edit" />}
                      variant="secondary"
                    >
                      <FormattedMessage {...st['shared.edit']} />
                    </Button>
                  </Box>
                  {!!client?.requiredScopes?.length && client?.requiredScopes.map((requiredScopeName, index) => (
                    <Chip size="small" id={requiredScopeName} className={classes["chip"]} key={`required-scope-${index}`}>{requiredScopeName}</Chip>
                  ))}
                </Box>}
                {/* Implicit scopes */}
                {userType.isAdmin && <Box>
                  <Box className={classes['alignTwoItemsSideWays']}>
                    <KeyValView
                      topBottomMargin
                      heading={<FormattedMessage {...t['client-edit.tab.implicit.scopes']} />}
                      subheading={<FormattedMessage {...t['client-edit.tab.implicit.scopes.subheading']} />}
                    />
                    <Button
                      data-testid="implicit-scope-button"
                      role="button"
                      onClick={onImplicitScopesEditClick}
                      leadingIcon={<EditOutlinedIcon data-testid="implicit-permissions-edit" />}
                      variant="secondary"
                   >
                      <FormattedMessage {...st['shared.edit']} />
                    </Button>
                  </Box>
                  {!!client?.implicitScopes?.length && client?.implicitScopes.map((implicitScopeName, index) => (
                    <Chip size="small" id={implicitScopeName} className={classes["chip"]} key={`implicit-scope-${index}`}>{implicitScopeName}</Chip>
                  ))}
                </Box>}

                {/* restricted scopes */}
                {!userType.isAdmin && !!restrictedScopes?.length && <Box>
                  <Box className={classes['alignTwoItemsSideWays']}>
                    <KeyValView
                      topBottomMargin
                      heading={<FormattedMessage {...t['client-edit.tab.restricted.scopes']} />}
                      subheading={<FormattedMessage {...t['client-edit.tab.restricted.scopes.subheading']} />}
                    />
                    {userType.isAdmin && <Button
                      data-testid="required-scope-button"
                      role="button"
                      onClick={onRequiredScopesEditClick}
                      leadingIcon={<EditOutlinedIcon data-testid="required-permissions-edit" />}
                      variant="secondary"
                    >
                      <FormattedMessage {...st['shared.edit']} />
                    </Button>}
                  </Box>
                  {!!restrictedScopes?.length && restrictedScopes.map((restrictedScopeName, index) => (
                    <Chip size="small" id={restrictedScopeName} className={classes["chip"]} key={`restricted-scope-${index}`}>{restrictedScopeName}</Chip>
                  ))}
                </Box>}

              </BoxedContainer>
              <MenuSeparator className={commonClass['seperator']} />
              {/* Authorities section */}
              {userType.isAdmin &&
                <React.Fragment>
                  <BoxedContainer
                    title={<FormattedMessage {...t['client-edit.tab.authorities']} />}
                    buttonIcon={<EditOutlinedIcon data-testid="permissions-edit" />}
                    buttonText={intl.formatMessage(st["shared.edit"])}
                    showCTAButton
                    ref={sectionRefs['Authorities']}
                    onClick={onRolesEditClick}
                  >
                    <Box className={commonClass['keyValue']} style={{ marginTop: 0 }}>
                      <KeyValView
                        topBottomMargin
                        heading={<FormattedMessage {...t['client-edit.authorities.roles.title']} />}
                        subheading={<FormattedMessage {...t['client-edit.authorities.roles.subheading']} />}
                      />
                    </Box>
                    <Box>
                      {!!client?.roles?.length && client?.roles.map((roleName, index) => (
                        <Chip size="small" id={roleName} className={classes["chip"]} key={`role-name-${index}`}>{roleName}</Chip>
                      ))}
                    </Box>
                  </BoxedContainer>
                  <MenuSeparator className={commonClass['seperator']} />
                </React.Fragment>}
              {/*Grant types*/}
              <BoxedContainer
                ref={sectionRefs['Grant_types']}
                title={<FormattedMessage {...t['client-edit.grant-types.title']} />}
                showCTAButton={false}
              >
                <Box className={commonClass['keyValue']}>
                  <KeyValView
                    heading={<FormattedMessage {...t['client-edit.grant-types.implicit.label']} />}
                    subheading={<FormattedMessage {...t['client-edit.grant-types.implicit.help']} />}
                  />
                  <Switch disabled={userType.isInternalDev} checked={client?.grants?.includes("implicit") || false} onChange={onGrantTypeChange("implicit")} />
                </Box>
                <Box className={commonClass['keyValue']}>
                  <KeyValView
                    heading={<FormattedMessage {...t['client-edit.grant-types.authorization-code.label']} />}
                    subheading={<FormattedMessage {...t['client-edit.grant-types.authorization-code.help']} />}
                  />
                  <Switch disabled={false} checked={client?.grants?.includes("authorization_code") || false} onChange={onGrantTypeChange("authorization_code")} />
                </Box>
                <Box className={commonClass['keyValue']}>
                  <KeyValView
                    heading={<FormattedMessage {...t['client-edit.grant-types.refresh-token.label']} />}
                    subheading={<FormattedMessage {...t['client-edit.grant-types.refresh-token.help']} />}
                  />
                  <Switch checked={client?.grants?.includes("refresh_token") || false} onChange={onGrantTypeChange("refresh_token")} />
                </Box>
                {!userType.isExternalDev && <React.Fragment>
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.grant-types.password.label']} />}
                      subheading={<FormattedMessage {...t['client-edit.grant-types.password.help']} />}
                    />
                    <Switch disabled={!userType.isAdmin} checked={client?.grants?.includes("password") || false} onChange={onGrantTypeChange("password")} />
                  </Box>
                  <Box className={commonClass['keyValue']}>
                    <KeyValView
                      heading={<FormattedMessage {...t['client-edit.grant-types.client-credentials.label']} />}
                      subheading={<FormattedMessage {...t['client-edit.grant-types.client-credentials.help']} />}
                    />
                    <Switch disabled={!userType.isAdmin} checked={client?.grants?.includes("client_credentials") || false} onChange={onGrantTypeChange("client_credentials")} />
                  </Box>
                </React.Fragment>}
                <Box className={commonClass['keyValue']}>
                  <KeyValView
                    heading={<FormattedMessage {...t['client-edit.grant-types.pat.label']} />}
                    subheading={<FormattedMessage {...t['client-edit.grant-types.pat.help']} />}
                  />
                  <Switch disabled={false} checked={client?.grants?.includes("personal_access_token") || false} onChange={onGrantTypeChange("personal_access_token")} />
                </Box>
              </BoxedContainer>
              <MenuSeparator className={commonClass['seperator']} />

              {/* secrets section */}
              {userType.isOwnedAccount &&
                <Box ref={sectionRefs['Secrets']}>
                  <ClientSecretCreateContainer client={client} />
                </Box>}
            </Box>
          </Box>

          {isEditMode && (
            <Box className={classes["inlineActionButtons"]}>
              <Button
                data-testid="inline-cancel-btn"
                variant="tertiary"
                onClick={navToClientList}
              >
                <FormattedMessage {...st["shared.cancel"]} />
              </Button>
              <Button
                data-testid="inline-save-btn"
                onClick={onSave}
                disabled={!enableCTAs || !canWrite}
              >
                {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                <FormattedMessage {...st['shared.save']} />}
              </Button>
            </Box>
          )}

          {/* ownership dialog */}
          <Dialog
            title={<FormattedMessage {...t["client-edit.owners.modal.title"]} />}
            open={openOwnershipEditModel}
            closable
            closeButtonLabel="close"
            onClosed={closeEditModel}
            actions={
              <React.Fragment>
                <Button data-testid="details-saveBtn" disabled={!enableCTAs || !canWrite || disableSubmit} onClick={onSave}>
                  {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                  <FormattedMessage {...st['shared.save']} />}
                </Button>
                <Button
                  data-testid="details-canBtn"
                  onClick={onCancel}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <Box className={classes["dialogChildren"]}>
              <Box className={classes["detailsTab"]}>
                <ClientOwnersTab
                  client={client}
                  onChange={onChange}
                  onBlur={onBlur}
                  errors={errors}
                  addOwnerEmail={addOwnerEmail}
                  onOwnerEmailChange={onOwnerEmailChange}
                  onOwnerEmailBlur={onOwnerEmailBlur}
                  onAddOwner={onAddOwner}
                  isOwnerEmailValid={isOwnerEmailValid}
                  onDeleteOwner={onDeleteOwner}
                  canWrite={canWrite}
                  owners={owners}
                  userType={userType}
                />
              </Box>
            </Box>
          </Dialog>

          {/* details Dialog */}
          <Dialog
            title={<FormattedMessage {...t["client-edit.edit.title"]} />}
            open={openDetailsEditModel}
            closable
            size="large"
            closeButtonLabel="close"
            onClosed={closeEditModel}
            actions={
              <React.Fragment>
                <Button data-testid="details-saveBtn" disabled={!enableCTAs || !canWrite || disableSubmit} onClick={onSave}>
                  {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                  <FormattedMessage {...st['shared.save']} />}
                </Button>
                <Button
                  data-testid="details-canBtn"
                  onClick={onCancel}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <Box className={classes["dialogChildren"]}>
              <Box className={classes["detailsTab"]}>
                <ClientDetailsTab
                  client={client}
                  onChange={onChange}
                  onBlur={onBlur}
                  errors={errors}
                  addRedirectURI={addRedirectURI}
                  onRedirectUriChange={onRedirectUriChange}
                  onAddUriClick={onAddUriClick}
                  isRedirectUriValid={isRedirectUriValid}
                  onDeleteUriClick={onDeleteUriClick}
                  canWrite={canWrite}
                />
              </Box>
            </Box>
          </Dialog>
          {/* scope dialogeu */}
          <ResponsiveDialog
            title={<FormattedMessage {...t["client-edit.tab.edit-scopes"]} />}
            open={openPermissionsEditModel}
            closable
            size="large"
            onClosed={closeEditModel}
            actions={
              <React.Fragment>
                <Button data-testid="per-saveBtn" disabled={!enableCTAs || !canWrite} onClick={onSave}>
                  {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                  <FormattedMessage {...st['shared.save']} />}
                </Button>
                <Button
                  data-testid="per-canBtn"
                  onClick={onCancel}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <Box className={classes["detailsTab"]}>
              {!canWrite &&
                <Box id="autoApproved1" data-testid="autoApproved1" className={`row ${classes["internalClient"]}`}>
                  <Box className="col-xs-12">
                    <Box>
                      <AlertV2>
                        <FormattedMessage {...st['shared.internal.client.message']} />
                      </AlertV2>
                    </Box>
                  </Box>
                </Box>}
              <Typography variant="heading-small">
                <FormattedMessage {...t["client-edit.tab.scopes"]} />
              </Typography>
              <Typography
                variant="body-medium"
              >
                <FormattedMessage
                  {...t["client-edit.permissions.instructions"]}
                />
              </Typography>
              {Object.keys(permissionsList).map((keyName, i) => (
                <PermissionItemView
                  id="permissionsView"
                  key={`permission-view-${keyName}`}
                  permissionData={permissionsList[keyName]}
                  isProfilePermission={
                    permissionsList[keyName].name.toLowerCase() === "profile"
                  }
                  onPermissionCheckboxClick={permissionCheckboxClick}
                  onPermissionGroupCheckboxClick={permissionGroupCheckboxClick}
                  togglePermissionsDetail={togglePermissionsDetail}
                  disabled={!canWrite}
                  meLocale={meLocale?.split('_')[0]}
                />
              ))}
            </Box>
          </ResponsiveDialog>

          {/* roles dialog */}
          <Dialog
            title={<FormattedMessage {...t["client-edit.roles.modal.title"]} />}
            open={openRolesEditModel}
            closable
            closeButtonLabel="close"
            onClosed={closeEditModel}
            actions={
              <React.Fragment>
                <Button data-testid="roles-saveBtn" disabled={!enableCTAs || !canWrite} onClick={onSave}>
                  {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                  <FormattedMessage {...st['shared.save']} />}
                </Button>
                <Button
                  data-testid="roles-canBtn"
                  onClick={onCancel}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <Box className={classes["dialogChildren"]}>
              <Box className={classes["detailsTab"]}>
                <ClientRolesTab
                  client={client}
                  canWrite={canWrite}
                  roles={client?.roles || []}
                  onAddRole={onAddRole}
                  onDeleteRole={onDeleteRole}
                  scopeValidationErrors={scopeValidationErrors?.roles}
                  clearScopeError={() => clearScopeError('roles')}
                />
              </Box>
            </Box>
          </Dialog>

          {/* implicit scopes dialog */}
          <Dialog
            variant="small"
            closable
            open={openImplicitScopesEditModel}
            onClosed={closeEditModel}
            title={<FormattedMessage {...t['client-edit.implicit-scopes.modal.title']} />}
            actions={
              <React.Fragment>
                <Button data-testid="implicit-scopes-saveBtn" disabled={!enableCTAs || !canWrite} onClick={onSave}>
                  {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                  <FormattedMessage {...st['shared.save']} />}
                </Button>
                <Button
                  data-testid="implicit-scopes-canBtn"
                  onClick={onCancel}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <Box className={classes["dialogChildren"]}>
              <Box className={classes["detailsTab"]}>
                <ClientImplicitScopesTab
                  client={client}
                  canWrite={canWrite}
                  implicitScopes={client?.implicitScopes || []}
                  onAddImplicitScope={onAddImplicitScope}
                  onDeleteImplicitScope={onDeleteImplicitScope}
                  scopeValidationErrors={scopeValidationErrors?.implicitScopes}
                  clearScopeError={() => clearScopeError('implicitScopes')}
                />
              </Box>
            </Box>
          </Dialog>

          {/* required scopes dialog */}
          <Dialog
            variant="small"
            closable
            open={openRequiredScopesEditModel}
            onClosed={closeEditModel}
            title={<FormattedMessage {...t['client-edit.required-scopes.modal.title']} />}
            actions={
              <React.Fragment>
                <Button data-testid="required-scopes-saveBtn" disabled={!enableCTAs || !canWrite} onClick={onSave}>
                  {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                  <FormattedMessage {...st['shared.save']} />}
                </Button>
                <Button
                  data-testid="required-scopes-canBtn"
                  onClick={onCancel}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <Box className={classes["dialogChildren"]}>
              <Box className={classes["detailsTab"]}>
                <ClientRequiredScopesTab
                  client={client}
                  canWrite={canWrite}
                  requiredScopes={client?.requiredScopes || []}
                  onAddRequiredScope={onAddRequiredScope}
                  onDeleteRequiredScope={onDeleteRequiredScope}
                  scopeValidationErrors={scopeValidationErrors?.requiredScopes}
                  clearScopeError={() => clearScopeError('requiredScopes')}
                />
              </Box>
            </Box>
          </Dialog>

          {/* scopes dialog for admin */}
          <Dialog
            variant="small"
            closable
            open={openScopesEditModel}
            onClosed={closeEditModel}
            title={<FormattedMessage {...t['client-edit.scopes.modal.title']} />}
            actions={
              <React.Fragment>
                <Button data-testid="scopes-saveBtn" disabled={!enableCTAs || !canWrite} onClick={onSave}>
                  {isLoading ? <FormattedMessage {...st['shared.saving']} /> :
                  <FormattedMessage {...st['shared.save']} />}
                </Button>
                <Button
                  data-testid="scopes-canBtn"
                  onClick={onCancel}
                  variant="tertiary">
                  <FormattedMessage {...st["shared.cancel"]} />
                </Button>
              </React.Fragment>
            }
          >
            <Box className={classes["dialogChildren"]}>
              <Box className={classes["detailsTab"]}>
                <ClientScopesAdminTab
                  client={client}
                  canWrite={canWrite}
                  scopes={client?.scopes || []}
                  onAddScope={onAddScope}
                  onDeleteScope={onDeleteScope}
                  rawScopeList={rawScopeList}
                  scopeValidationErrors={scopeValidationErrors?.scopes}
                  clearScopeError={() => clearScopeError('scopes')}
                />
              </Box>
            </Box>
          </Dialog>
        </Box>
        :
        //create client flow
        <Box className={classes["createClientMain"]}>
          <Box className={classes["headerSection"]}>
            <Typography variant="heading-small">
              <FormattedMessage {...t['client-create.add.title']} />
            </Typography>
            <IconButton
              data-testid="createClientCancelBtn"
              labelId={<FormattedMessage {...t["create-token.tab.cancel"]} />}
              onClick={navToClientList}
            >
              <CloseOutlinedIcon role="button" />
            </IconButton>
          </Box>
          <Box className={classes["progressBlock"]}>
            <Box className={classes["individualProgressBlock"]} />
            {(permissionsButtonClass === 'active' || permissionsButtonClass === 'completed') ? <Box className={classes["individualProgressBlock"]} /> : <Box className={classes["individualRemainingProgressBlock"]} />}
            {(permissionsButtonClass === 'completed' && detailsButtonClass === 'completed') ? <Box className={classes["individualProgressBlock"]} /> : <Box className={classes["individualRemainingProgressBlock"]} />}
          </Box>
          <Box className={classes["overflowContainer"]}>
            <Box className={classes["mainForm"]}>
              <Box>
                <Typography variant="heading-small">
                  {detailsButtonClass === "active" && <FormattedMessage {...t["client-create.tab.details"]} />}
                  {permissionsButtonClass === 'active' && <FormattedMessage {...t['client-edit.tab.scopes']} />}
                  {permissionsButtonClass === 'completed' && detailsButtonClass === 'completed' && <FormattedMessage {...t["create-token.tab.copy-and-store"]} />}
                </Typography>
              </Box>
              {detailsButtonClass === 'active' && <Box id="detailsContents" className={`${detailsContentsClass}`}>
                <ClientDetailsTab
                  client={client}
                  onChange={onChange}
                  onBlur={onBlur}
                  errors={errors}
                  addRedirectURI={addRedirectURI}
                  onRedirectUriChange={onRedirectUriChange}
                  onAddUriClick={onAddUriClick}
                  isRedirectUriValid={isRedirectUriValid}
                  onDeleteUriClick={onDeleteUriClick}
                  canWrite={canWrite}
                />
              </Box>}
              {permissionsButtonClass === 'active' &&
                <Box id="permissionsContents" className={`${permissionsContentsClass}`}>
                  <Typography
                    variant="body-medium"
                    color="rgba(37, 40, 45, 1)"
                    className={commonClass["general-text"]}
                  >
                    <FormattedMessage
                      {...t["client-edit.permissions.instructions"]}
                    />
                  </Typography>
                  {Object.keys(permissionsList).map((keyName, i) => (
                    <PermissionItemView
                      id="permissionsView"
                      key={`permission-view-${keyName}`}
                      permissionData={permissionsList[keyName]}
                      isProfilePermission={
                        permissionsList[keyName].name.toLowerCase() === "profile"
                      }
                      onPermissionCheckboxClick={permissionCheckboxClick}
                      onPermissionGroupCheckboxClick={permissionGroupCheckboxClick}
                      togglePermissionsDetail={togglePermissionsDetail}
                      disabled={!canWrite}
                      meLocale={meLocale?.split('_')[0]}
                    />
                  ))}
                </Box>}
              {permissionsButtonClass === 'completed' && detailsButtonClass === 'completed' &&
                <Box id="credentialsContents" className={`${credentialsContentsClass}`}>
                  <AlertV2 className={classes["clientSecretAlertInfo"]}>
                    <FormattedMessage {...t['create-token.credentials.copy-display-text']} values={{ lineBreak: <br /> }} />
                  </AlertV2>
                  <Box className={classes["clientIdSection"]}>
                    <Typography variant="body-medium-strong" color="type-color-default">
                      <FormattedMessage {...st['shared.client-id']} />
                    </Typography>
                    <Box className={classes["tokenDisplay"]}>
                      <Typography variant="button-small">
                        {newClientCredentials.id ? newClientCredentials.id : ''}
                      </Typography>
                      <Box className={classes["copyButtonStyle"]}>
                        <Button
                          data-testid="copyClientSecretBtn"
                          title="copyButton"
                          leadingIcon={<CopyOutlinedIcon color="#2C72DE" />}
                          variant="tertiary"
                          onClick={onCopy(newClientCredentials.id, true)}
                        >
                          <Typography variant="body-small-strong" color="type-color-brand-default">
                            <FormattedMessage {...st['shared.copy']} />
                          </Typography>
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  {newClientCredentials.secret &&
                    <Box className={classes["clientIdSection"]}>
                      <Typography variant="body-medium-strong" color="type-color-default">
                        <FormattedMessage {...st['shared.credentials.client.secret.title']} />
                      </Typography>
                      <Box className={classes["tokenDisplayBg"]}>
                        <Typography variant="button-small">
                          {newClientCredentials.secret}
                        </Typography>
                        <Box className={`${classes["flexSideSmall"]} ${classes["copyButtonStyle"]}`}>
                          <Button
                            title="secretCopyButton"
                            leadingIcon={<CopyOutlinedIcon color="#2C72DE" />}
                            variant="tertiary"
                            onClick={onCopy(newClientCredentials.secret, false)}
                          >
                            <Typography variant="body-small-strong" color="type-color-brand-default">
                              <FormattedMessage {...st['shared.copy']} />
                            </Typography>
                          </Button>
                        </Box>
                      </Box>
                      <Box>
                        <Checkbox
                          id="confirmCopy"
                          name="confirmCopy"
                          checked={isDone}
                          onChange={onConfirmSecretCopy}
                          title={intl.formatMessage(t['create-token.token.copy.confirm'])}>
                          <FormattedMessage {...t['create-token.token.copy.confirm']} />
                        </Checkbox>
                      </Box>
                    </Box>}
                </Box>}
            </Box>
          </Box>
          <Box className={classes["ctaSection"]}>
            {detailsButtonClass === 'active' &&
              <React.Fragment>
                <Button onClick={navToClientList} variant="neutral" leadingIcon={<ChevronLeftFilledIcon />}>
                  {intl.formatMessage(st["shared.back"])}
                </Button>
                <Button
                  id="permissionsTabBtn"
                  onClick={tabClick}
                  disabled={!(client.name.length && (client.redirectUris.length || addRedirectURI.trim().length))}
                  style={{ marginLeft: "auto" }}
                  title={intl.formatMessage(t['create-token.tab.next'])}>
                  {intl.formatMessage(t["create-token.tab.next"])}
                </Button>
              </React.Fragment>}
            {permissionsButtonClass === 'active' &&
              <React.Fragment>
                <Button
                  id="detailsTabBtn"
                  type="button"
                  onClick={tabClick}
                  variant="neutral"
                  leadingIcon={<ChevronLeftFilledIcon />}>
                  {intl.formatMessage(st["shared.back"])}
                </Button>
                <Button
                  id="btn-save"
                  disabled={!enableCTAs}
                  onClick={onSave}
                  style={{ marginLeft: "auto" }}
                >
                  {intl.formatMessage(t["create-token.tab.next"])}
                </Button>
              </React.Fragment>}
            {permissionsButtonClass === 'completed' && detailsButtonClass === 'completed' &&
              <Box className={classes['doneButton']}>
                <Button
                  id="btn-done"
                  disabled={!isDone}
                  onClick={onDoneClick}>
                  <FormattedMessage {...st['shared.done']} />
                </Button>
              </Box>}
          </Box>
        </Box>}
    </React.Fragment>);
};

ClientEdit.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  client: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  tabClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRedirectUriChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDoneClick: PropTypes.func.isRequired,
  isDone: PropTypes.bool.isRequired,
  onConfirmSecretCopy: PropTypes.func.isRequired,
  addRedirectURI: PropTypes.string.isRequired,
  onAddUriClick: PropTypes.func.isRequired,
  permissionCheckboxClick: PropTypes.func.isRequired,
  permissionGroupCheckboxClick: PropTypes.func.isRequired,
  isRedirectUriValid: PropTypes.bool.isRequired,
  onDeleteUriClick: PropTypes.func.isRequired,
  enableCTAs: PropTypes.bool.isRequired,
  canCancel: PropTypes.bool.isRequired,
  detailsContentsClass: PropTypes.string.isRequired,
  permissionsContentsClass: PropTypes.string.isRequired,
  credentialsContentsClass: PropTypes.string.isRequired,
  detailsButtonClass: PropTypes.string.isRequired,
  permissionsButtonClass: PropTypes.string.isRequired,
  credentialsButtonClass: PropTypes.string.isRequired,
  permissionsList: PropTypes.object.isRequired,
  togglePermissionsDetail: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  newClientCredentials: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  navToClientList: PropTypes.func.isRequired,
  onDetailsEditClick: PropTypes.func.isRequired,
  openDetailsEditModel: PropTypes.bool.isRequired,
  onPermissionsEditClick: PropTypes.func.isRequired,
  openPermissionsEditModel: PropTypes.bool.isRequired,
  closeEditModel: PropTypes.func.isRequired,
  clientData: PropTypes.object.isRequired,
  canWrite: PropTypes.bool.isRequired,
  disableSubmit: PropTypes.bool.isRequired,
  meLocale: PropTypes.string.isRequired,
  onOwnershipEditClick: PropTypes.func.isRequired,
  openOwnershipEditModel: PropTypes.bool.isRequired,
  onRolesEditClick: PropTypes.func.isRequired,
  openRolesEditModel: PropTypes.bool.isRequired,
  owners: PropTypes.array,
  sideNavTop: PropTypes.number.isRequired,
  sideNavRef: PropTypes.object.isRequired,
  sectionRefs: PropTypes.object.isRequired,
  onMenuClick: PropTypes.func.isRequired,
  activeSection: PropTypes.string.isRequired,
  onGrantTypeChange: PropTypes.func.isRequired,
  onAuthorizationChange: PropTypes.func.isRequired,
  onAddRole: PropTypes.func.isRequired,
  onDeleteRole: PropTypes.func.isRequired,
  onImplicitScopesEditClick: PropTypes.func.isRequired,
  openImplicitScopesEditModel: PropTypes.bool.isRequired,
  onAddImplicitScope: PropTypes.func.isRequired,
  onDeleteImplicitScope: PropTypes.func.isRequired,
  onRequiredScopesEditClick: PropTypes.func.isRequired,
  openRequiredScopesEditModel: PropTypes.bool.isRequired,
  onAddRequiredScope: PropTypes.func.isRequired,
  onDeleteRequiredScope: PropTypes.func.isRequired,
  onScopesEditClick: PropTypes.func.isRequired,
  openScopesEditModel: PropTypes.bool.isRequired,
  onAddScope: PropTypes.func.isRequired,
  onDeleteScope: PropTypes.func.isRequired,
  rawScopeList: PropTypes.array.isRequired,
  scopeValidationErrors: PropTypes.object,
  clearScopeError: PropTypes.func.isRequired,
  addOwnerEmail: PropTypes.string,
  onOwnerEmailChange: PropTypes.func.isRequired,
  onOwnerEmailBlur: PropTypes.func.isRequired,
  onAddOwner: PropTypes.func.isRequired,
  isOwnerEmailValid: PropTypes.bool.isRequired,
  onDeleteOwner: PropTypes.func.isRequired,
  userType: PropTypes.shape({
    isAdmin: PropTypes.bool.isRequired,
    isInternalDev: PropTypes.bool.isRequired,
    isExternalDev: PropTypes.bool.isRequired,
    isOwnedAccount: PropTypes.bool.isRequired
  }).isRequired,
  restrictedScopes: PropTypes.array.isRequired
};

export default ClientEdit;
